import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Plus, MoreHorizontal, CheckCircle2, XCircle,
  Clock, CalendarDays, IndianRupee, Phone, Sparkle, CalendarClock, Loader2,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import PhoneInput from "@/components/PhoneInput";
import { isSubmittablePhone } from "@/lib/phone";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CalendarPopover } from "./appointments/CalendarPopover";
import RescheduleModal from "./appointments/RescheduleModal";
import "./Appointments.css";

const STATUS_META = {
  confirmed:   { label: "Confirmed",   chip: "lux-chip-accent" },
  rescheduled: { label: "Rescheduled", chip: "lux-chip-warning" },
  completed:   { label: "Completed",   chip: "lux-chip-success" },
  cancelled:   { label: "Cancelled",   chip: "lux-chip-danger" },
};
const padDatePart = (n) => String(n).padStart(2, "0");

function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
}
function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase() || "•";
}
function formatDay(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

// Calender view - add apppointment 

function parseAppointmentDateTime(value) {
  const match = String(value || "").match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/
  );

  if (match) {
    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4]),
      Number(match[5])
    );
  }

  const now = new Date();
  return new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    10,
    0
  );
}

function CompactDateTimePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const selectedDate = parseAppointmentDateTime(value);
  const pickerRef = useRef(null);

  const [viewDate, setViewDate] = useState(() => {
    const date = parseAppointmentDateTime(value);
    return new Date(date.getFullYear(), date.getMonth(), 1);
  });

  useEffect(() => {
    if (!open) return;

    const handleOutsideClick = (event) => {
      if (!pickerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const writeDateTime = (
    year,
    month,
    day,
    hour = selectedDate.getHours(),
    minute = selectedDate.getMinutes()
  ) => {
    const next = new Date(year, month, day, hour, minute);

    const formatted =
      `${next.getFullYear()}-${padDatePart(next.getMonth() + 1)}-` +
      `${padDatePart(next.getDate())}T` +
      `${padDatePart(next.getHours())}:${padDatePart(next.getMinutes())}`;

    onChange(formatted);
  };

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstWeekday = new Date(year, month, 1).getDay();

  const days = Array.from({ length: 42 }, (_, index) =>
    new Date(year, month, index - firstWeekday + 1)
  );

  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const displayValue = value
    ? `${padDatePart(selectedDate.getDate())}-${padDatePart(
        selectedDate.getMonth() + 1
      )}-${selectedDate.getFullYear()} ${padDatePart(
        selectedDate.getHours()
      )}:${padDatePart(selectedDate.getMinutes())}`
    : "Select date and time";

  return (
    <div className="nexa-dtp" ref={pickerRef}>
      <button
        type="button"
        className="lux-input nexa-dtp-trigger"
        data-testid="apt-form-start"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setViewDate(
            new Date(
              selectedDate.getFullYear(),
              selectedDate.getMonth(),
              1
            )
          );
          setOpen((current) => !current);
        }}
      >
        <span>{displayValue}</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          aria-hidden="true"
        >
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M16 3v4M8 3v4M3 10h18" />
        </svg>
      </button>

      {open && (
        <div
          className="nexa-dtp-popup"
          role="dialog"
          aria-label="Choose appointment date and time"
        >
          <div className="nexa-dtp-header">
            <div className="nexa-dtp-nav">
              <button
                type="button"
                aria-label="Previous year"
                title="Previous year"
                onClick={() => setViewDate(new Date(year - 1, month, 1))}
              >
                «
              </button>

              <button
                type="button"
                aria-label="Previous month"
                title="Previous month"
                onClick={() => setViewDate(new Date(year, month - 1, 1))}
              >
                ‹
              </button>
            </div>

            <strong>
              {viewDate.toLocaleDateString("en-IN", {
                month: "long",
                year: "numeric",
              })}
            </strong>

            <div className="nexa-dtp-nav">
              <button
                type="button"
                aria-label="Next month"
                title="Next month"
                onClick={() => setViewDate(new Date(year, month + 1, 1))}
              >
                ›
              </button>

              <button
                type="button"
                aria-label="Next year"
                title="Next year"
                onClick={() => setViewDate(new Date(year + 1, month, 1))}
              >
                »
              </button>
            </div>
          </div>

          <div className="nexa-dtp-calendar">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <span className="nexa-dtp-weekday" key={day}>
                {day}
              </span>
            ))}

            {days.map((day) => {
              const isCurrentMonth = day.getMonth() === month;
              const isSelected = sameDay(day, selectedDate);
              const isToday = sameDay(day, new Date());

              return (
                <button
                  key={`${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`}
                  type="button"
                  className={[
                    "nexa-dtp-day",
                    !isCurrentMonth && "is-outside",
                    isSelected && "is-selected",
                    isToday && "is-today",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => {
                    writeDateTime(
                      day.getFullYear(),
                      day.getMonth(),
                      day.getDate()
                    );
                    if (!isCurrentMonth) {
                      setViewDate(
                        new Date(day.getFullYear(), day.getMonth(), 1)
                      );
                    }
                  }}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>

          <div className="nexa-dtp-time">
            <span>Time</span>

            <select
              aria-label="Appointment hour"
              value={padDatePart(selectedDate.getHours())}
              onChange={(event) =>
                writeDateTime(
                  selectedDate.getFullYear(),
                  selectedDate.getMonth(),
                  selectedDate.getDate(),
                  Number(event.target.value),
                  selectedDate.getMinutes()
                )
              }
            >
              {Array.from({ length: 24 }, (_, hour) => (
                <option key={hour} value={padDatePart(hour)}>
                  {padDatePart(hour)}
                </option>
              ))}
            </select>

            <span className="nexa-dtp-colon">:</span>

            <select
              aria-label="Appointment minute"
              value={padDatePart(selectedDate.getMinutes())}
              onChange={(event) =>
                writeDateTime(
                  selectedDate.getFullYear(),
                  selectedDate.getMonth(),
                  selectedDate.getDate(),
                  selectedDate.getHours(),
                  Number(event.target.value)
                )
              }
            >
              {Array.from({ length: 60 }, (_, minute) => (
                <option key={minute} value={padDatePart(minute)}>
                  {padDatePart(minute)}
                </option>
              ))}
            </select>
          </div>

          <div className="nexa-dtp-footer">
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                writeDateTime(
                  today.getFullYear(),
                  today.getMonth(),
                  today.getDate()
                );
                setViewDate(
                  new Date(today.getFullYear(), today.getMonth(), 1)
                );
              }}
            >
              Today
            </button>

            <button
              type="button"
              className="nexa-dtp-done"
              onClick={() => setOpen(false)}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Build the "· WhatsApp …" tail from a backend notification result.
// notification shape: { success, provider, message_id, error } or null.
function waTail(notification, customerName = "") {
  if (!notification) return "";
  const who = customerName ? ` to ${customerName}` : " to customer";
  if (notification.success) return `WhatsApp sent${who}.`;
  const err = notification.error || "";
  if (/phone/i.test(err)) return "No customer phone on file — WhatsApp skipped.";
  return `WhatsApp not sent: ${err || "delivery failed"}.`;
}
function buildLocalInsight(rows, summary) {
  // Client-side executive brief for dates other than today.
  const total = rows?.length || 0;
  const vipCount = summary?.vip_count || 0;
  const pendingPay = (rows || []).filter((r) => r.status === "completed" && r.payment_status === "payment_pending").length;
  const summaryLines = [];
  if (total === 0) summaryLines.push("A quiet day — nothing scheduled.");
  else summaryLines.push(total <= 15 ? "The day looks balanced." : "A busy day ahead.");
  summaryLines.push(total ? `${total} appointment${total > 1 ? "s" : ""}.` : "");
  if (vipCount) summaryLines.push(`${vipCount} VIP customer${vipCount > 1 ? "s" : ""}.`);
  if (pendingPay) summaryLines.push(`${pendingPay} awaiting payment.`);
  return {
    summary: summaryLines.filter(Boolean),
    revenue: summary?.expected_revenue || 0,
    recommendation: pendingPay
      ? `${pendingPay} appointment${pendingPay > 1 ? "s" : ""} awaiting payment.`
      : (total ? "The day is prepared. Nothing needs your attention." : null),
  };
}

export default function Appointments() {

  const [rows, setRows] = useState(null); // null = loading
  const [summary, setSummary] = useState(null);
  const [insight, setInsight] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [rescheduling, setRescheduling] = useState(null); // appointment object or null
  const [completing, setCompleting] = useState(null);     // appointment object or null
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [markedDates, setMarkedDates] = useState(new Set());
  const calBtnRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(null);

  useEffect(() => {
    const close = () => setMenuOpen(null);

    document.addEventListener("click", close);

    return () => document.removeEventListener("click", close);
  }, []);

  const isToday = selectedDate === new Date().toISOString().slice(0, 10);
  const heading = useMemo(() => {
    const d = new Date(selectedDate + "T00:00:00");
    return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  }, [selectedDate]);

  const load = useCallback(async () => {
    try {
      const [{ data: a }, { data: s }, { data: i }] = await Promise.all([
        api.get("/appointments", { params: { date: selectedDate } }),
        api.get("/appointments/summary", { params: { date: selectedDate } }),
        api.get("/appointments/insight"),
      ]);
      setRows(a.appointments || []);
      setSummary(s);
      setInsight(i);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load appointments");
      setRows([]);
    }
  }, [selectedDate]);

  // Fetch all appointments once → derive dot indicators for the calendar
  const loadMarked = useCallback(async () => {
    try {
      const { data } = await api.get("/appointments");
      const s = new Set();
      (data.appointments || []).forEach((r) => r.date && s.add(r.date));
      setMarkedDates(s);
    } catch { /* silent — dots are non-critical */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMarked(); }, [loadMarked]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
    const okS =
      statusFilter === "all"
        ? true
        : statusFilter === "ai"
          ? r.booking_source === "ai_receptionist"
          : r.status === statusFilter;

      const okQ = !q || [r.customer_name, r.phone, r.service, r.appointment_id].some((v) => (v || "").toLowerCase().includes(q));
      return okS && okQ;
    });
  }, [rows, query, statusFilter]);

  const cancelAppointment = async (row) => {
    try {
      const { data } = await api.patch(`/appointments/${row.appointment_id}`, { status: "cancelled" });
      const tail = waTail(data?.notification, row.customer_name);
      toast.success("Appointment cancelled.", { description: tail || undefined });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const completeAppointment = async (row, paymentReceived) => {
    try {
      const { data } = await api.post(`/appointments/${row.appointment_id}/complete`, { payment_received: paymentReceived });
      const tail = waTail(data?.notification, row.customer_name);
      const desc = [
        paymentReceived ? "Payment recorded." : "Payment pending.",
        tail,
      ].filter(Boolean).join(" ");
      toast.success(
        paymentReceived ? "Appointment completed successfully." : "Appointment completed.",
        { description: desc },
      );
      setCompleting(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const markPaid = async (id) => {
    try {
      await api.post(`/appointments/${id}/mark-paid`);
      toast.success("Payment recorded successfully.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="nexa-appointments-shell min-h-screen">
      <Sidebar />
      <main className="nexa-compact nexa-appointments-main min-h-screen lg:ml-[236px]">
        <div className="nexa-appointments-page w-full">
        {/* Appointments header */}
        <motion.header
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="nexa-appointments-header"
        >
          <div className="nexa-appointments-heading">
            <div className="nexa-apt-date-label" data-testid="apt-date">{heading}</div>
          </div>

          <div className="nexa-apt-toolbar">
            <label className="nexa-apt-search" aria-label="Search appointments">
              <Search size={15} strokeWidth={1.7} aria-hidden="true" />
              <input
                data-testid="apt-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search appointments"
              />
            </label>

            <div className="nexa-apt-calendar-wrap">
              <button
                ref={calBtnRef}
                onClick={() => setCalendarOpen((o) => !o)}
                className={`nexa-apt-icon-button ${calendarOpen ? "is-open" : ""}`}
                aria-label="Open calendar"
                aria-expanded={calendarOpen}
                data-testid="apt-calendar-toggle"
              >
                <CalendarDays size={16} strokeWidth={1.7} />
              </button>
              {calendarOpen && (
                <CalendarPopover
                  value={selectedDate}
                  onChange={(iso) => { setSelectedDate(iso); }}
                  onClose={() => setCalendarOpen(false)}
                  markedDates={markedDates}
                  anchorRef={calBtnRef}
                />
              )}
            </div>

            <button
              data-testid="apt-add-btn"
              onClick={() => setCreating(true)}
              className="nexa-apt-add-button"
            >
              <Plus size={15} strokeWidth={2} />
              <span>Add appointment</span>
            </button>
          </div>
        </motion.header>

        {/* AI appointment briefing */}
        {(() => {
          const displayInsight = isToday ? insight : (rows && summary ? buildLocalInsight(rows, summary) : null);
          if (!displayInsight) return null;
          return (
            <motion.section
              data-testid="apt-insight"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="nexa-apt-brief"
              aria-label="AI appointment briefing"
            >
              <div className="nexa-apt-orbit-art" aria-hidden="true">
                <span className="nexa-apt-orbit-ring orbit-a" />
                <span className="nexa-apt-orbit-ring orbit-b" />
                <span className="nexa-apt-orbit-core">
                  <img src="/images/nexa-appointment-call.png" alt="" />
                </span>
                <span className="nexa-apt-orbit-light light-a" />
                <span className="nexa-apt-orbit-light light-b" />
              </div>

              <div className="nexa-apt-brief-copy">
                <div className="nexa-apt-brief-kicker">
                  <Sparkle size={14} strokeWidth={1.8} />
                  AI APPOINTMENT BRIEF
                </div>
                <h2>
                  {isToday ? "Today's schedule" : "Schedule overview"}
                </h2>
                <p>{(displayInsight.summary || []).join(" ")}</p>

                {displayInsight.recommendation && (
                  <div className="nexa-apt-next-action">
                    <span className="nexa-apt-next-dot" aria-hidden="true" />
                    <div>
                      <div className="nexa-apt-next-label">NEXA’S NEXT ACTION</div>
                      <div className="nexa-apt-next-text">{displayInsight.recommendation}</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="nexa-apt-brief-divider" aria-hidden="true" />

              <div className="nexa-apt-revenue-panel">
                <div className="nexa-apt-revenue-label">EXPECTED REVENUE</div>
                <div className="nexa-apt-revenue-value">
                  ₹{Number(displayInsight.revenue || 0).toLocaleString("en-IN")}
                </div>
                <div className="nexa-apt-revenue-foot">
                  <CalendarDays size={13} strokeWidth={1.7} />
                  {isToday ? "Today’s schedule" : "Selected date"}
                </div>
              </div>
            </motion.section>
          );
        })()}

        {/* Appointment summary */}
        {summary && (
          <section className="nexa-appt-summary-grid" data-testid="apt-summary" aria-label="Appointment summary">
            {[
              { l: "Appointments", v: summary.total, tone: "mint" },
              { l: "Confirmed", v: summary.by_status?.confirmed || 0, tone: "blue" },
              { l: "Rescheduled", v: summary.by_status?.rescheduled || 0, tone: "sand" },
              { l: "Cancelled", v: summary.by_status?.cancelled || 0, tone: "rose" },
              { l: "Completed", v: summary.by_status?.completed || 0, tone: "mint" },
            ].map((item, i) => (
              <motion.div
                key={item.l}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.035 * i }}
                className={`nexa-appt-summary-card tone-${item.tone}`}
              >
                <div className="nexa-appt-summary-label">{item.l}</div>
                <div className="nexa-appt-summary-value">{item.v ?? "—"}</div>
              </motion.div>
            ))}
          </section>
        )}

        {/* Status filters */}
        <div className="nexa-appt-list-toolbar" data-testid="apt-filter-chips">
          <div className="nexa-appt-list-heading">
            <div>
              <h2>Appointments</h2>
            </div>
            <span className="nexa-appt-result-count">
              {rows === null ? "Loading…" : `${filtered.length} ${filtered.length === 1 ? "booking" : "bookings"}`}
            </span>
          </div>
          <div className="nexa-appt-filter-row">
            {[["all", "All"], ["confirmed", "Confirmed"], ["rescheduled", "Rescheduled"], ["cancelled", "Cancelled"], ["completed", "Completed"],  ["ai", "AI Bookings"],].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setStatusFilter(k)}
                data-testid={`apt-filter-${k}`}
                aria-pressed={statusFilter === k}
                className={`nexa-appt-filter-chip ${statusFilter === k ? "is-active" : ""}`}
              >{label}</button>
            ))}
          </div>
        </div>

        {/* Appointment list */}
        <section className="nexa-appt-list">
          {rows === null && [1,2,3].map((i) => (
            <div key={i} className="nexa-appt-card nexa-appt-skeleton">
              <div className="lux-skel h-2.5 w-1/3 mb-2" />
              <div className="lux-skel h-2 w-2/3" />
            </div>
          ))}

          {rows && filtered.length === 0 && (
            <motion.div
              data-testid="apt-empty"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
              className="nexa-appt-empty"
            >
              <div className="nexa-appt-empty-icon">
                <CalendarDays size={17} strokeWidth={1.6} />
              </div>
              <h3 className="nexa-appt-empty-title">
                {isToday ? "No appointments yet." : `No appointments scheduled for ${formatDay(selectedDate)}.`}
              </h3>
              <p className="nexa-appt-empty-copy">
                {isToday
                  ? "When your day fills up, it will appear here — organised, quietly, ready for you."
                  : "Create one for this date, or pick another day from the calendar."}
              </p>
              <button onClick={() => setCreating(true)} className="nexa-apt-add-button mt-5" data-testid="apt-empty-cta">
                <Plus size={15} strokeWidth={2} /> Create appointment
              </button>
            </motion.div>
          )}

          <AnimatePresence initial={false}>
            {filtered.map((r, i) => {
              const meta = STATUS_META[r.status] || STATUS_META.confirmed;
              const paymentPending = r.status === "completed" && r.payment_status !== "paid";
              return (
                <motion.article
                  layout
                  key={r.appointment_id}
                  data-testid={`apt-card-${r.appointment_id}`}
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.35, delay: Math.min(0.03 * i, 0.2) }}
                  className="nexa-appt-card"
                >
                  <div className="nexa-appt-card-row">
                    {/* Time */}
                    <div className="nexa-appt-time">
                      <div className="nexa-appt-time-value">{fmtTime(r.start_time)}</div>
                      <div className="nexa-appt-duration">
                        <Clock size={12} strokeWidth={1.6} /> {r.duration_min} min
                      </div>
                    </div>

                    {/* Customer */}
                    <div className="nexa-appt-customer">
                      <div className="nexa-appt-avatar">{initials(r.customer_name)}</div>
                      <div className="nexa-appt-customer-copy">
                        
                        <div className="nexa-appt-customer-title">
                          <div className="nexa-appt-customer-name">{r.customer_name}</div>

                          {r.is_vip && (
                            <span className="nexa-appt-vip-badge">VIP</span>
                          )}

                          {r.booking_source === "ai_receptionist" && (
                            <span
                              className="nexa-appt-ai-badge"
                              title="Booked by AI Receptionist"
                              data-testid={`apt-ai-badge-${r.appointment_id}`}
                            >
                              <Sparkle size={12} strokeWidth={1.7} />
                              AI Booked
                            </span>
                          )}
                        </div>
                        
                        <div className="nexa-appt-meta">
                          <span className="nexa-appt-meta-service">
                            {r.service}
                          </span>

                          {r.staff && (
                            <span className="nexa-appt-meta-staff hidden md:inline">
                              {r.staff}
                            </span>
                          )}

                          {r.phone && (
                            <span className="nexa-appt-meta-phone hidden md:inline-flex">
                              <Phone size={12} strokeWidth={1.7} />
                              {r.phone}
                            </span>
                          )}
                        </div>

                      </div>
                    </div>

                    {/* Status + payment */}
                    <div className="nexa-appt-payment-group">
                      <span className={`nexa-appt-status ${r.status}`}>{meta.label}</span>
                      {r.payment_status === "paid" ? (
                        <span className="nexa-appt-payment paid"><IndianRupee size={12} /> Paid</span>
                      ) : paymentPending ? (
                        <button
                          data-testid={`apt-mark-paid-${r.appointment_id}`}
                          onClick={(e) => { e.stopPropagation(); markPaid(r.appointment_id); }}
                          className="nexa-appt-payment pending"
                          title="Mark as paid"
                        >
                          <IndianRupee size={12} /> Mark paid
                        </button>
                      ) : (
                        <span className="nexa-appt-payment"><IndianRupee size={12} /> ₹{Number(r.amount || 0).toLocaleString("en-IN")}</span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="nexa-appt-menu-wrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpen(
                            menuOpen === r.appointment_id ? null : r.appointment_id
                          );
                        }}
                        className="nexa-appt-menu-button"
                        data-testid={`apt-row-menu-${r.appointment_id}`}
                      >
                        <MoreHorizontal size={16} />
                      </button>

                      {menuOpen === r.appointment_id &&
                        (r.status === "confirmed" ||
                          r.status === "rescheduled" ||
                          paymentPending) && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="nexa-appt-row-menu"
                          style={{
                            boxShadow:
                              "0 14px 34px rgba(0,0,0,.16), 0 0 0 1px rgba(0,0,0,.03)",
                          }}
                        >
                          {(r.status === "confirmed" || r.status === "rescheduled") && (
                            <button
                              onClick={() => {
                                setCompleting(r);
                                setMenuOpen(null);
                              }}
                              className="nexa-appt-menu-item"
                              data-testid={`apt-menu-complete-${r.appointment_id}`}
                            >
                              <CheckCircle2 className="text-[#23815F]" size={14} />
                              Complete Appointment
                            </button>
                          )}

                          {r.status !== "cancelled" &&
                            r.status !== "completed" && (
                              <button
                                onClick={() => {
                                  setRescheduling(r);
                                  setMenuOpen(null);
                                }}
                                className="nexa-appt-menu-item"
                                data-testid={`apt-menu-reschedule-${r.appointment_id}`}
                              >
                                <CalendarClock className="text-[#0B6251]" size={14} />
                                Reschedule
                              </button>
                          )}

                          {paymentPending && (
                            <button
                              onClick={() => {
                                markPaid(r.appointment_id);
                                setMenuOpen(null);
                              }}
                              className="nexa-appt-menu-item"
                              data-testid={`apt-menu-mark-paid-${r.appointment_id}`}
                            >
                              <IndianRupee className="text-[#23815F]" size={14} />
                              Mark Paid
                            </button>
                          )}

                          {r.status !== "cancelled" &&
                            r.status !== "completed" && (
                              <>
                                <div className="border-t border-nexa-line" />

                                <button
                                  onClick={() => {
                                    cancelAppointment(r);
                                    setMenuOpen(null);
                                  }}
                                  className="nexa-appt-menu-item danger"
                                  data-testid={`apt-menu-cancel-${r.appointment_id}`}
                                >
                                  <XCircle size={14} />
                                  Cancel Appointment
                                </button>
                              </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quiet AI hint */}
                  {r.is_vip && (r.status === "confirmed" || r.status === "rescheduled") && (
                    <div className="nexa-appt-vip-note">
                      <Sparkle size={13} className="text-[#0B7962]" strokeWidth={1.7} />
                      VIP customer — prepare a warm welcome.
                    </div>
                  )}
                </motion.article>
              );
            })}
          </AnimatePresence>
        </section>
        </div>
      </main>

      {creating && <CreateModal defaultDate={selectedDate} onClose={() => setCreating(false)} onCreated={() => { load(); loadMarked(); }} />}
      {rescheduling && (
        <RescheduleModal
          appointment={rescheduling}
          onClose={() => setRescheduling(null)}
          onDone={(newDate) => { if (newDate) setSelectedDate(newDate); load(); loadMarked(); }}
        />
      )}
      {completing && (
        <CompleteAppointmentPopup
          appointment={completing}
          onClose={() => setCompleting(null)}
          onConfirm={(paymentReceived) => completeAppointment(completing, paymentReceived)}
        />
      )}
    </div>
  );
}

function CompleteAppointmentPopup({ appointment, onClose, onConfirm }) {
  const [choice, setChoice] = useState(null); // "yes" | "no"
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (choice === null) return;
    setSaving(true);
    try { await onConfirm(choice === "yes"); }
    finally { setSaving(false); }
  };
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="nexa-apt-modal-backdrop fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="apt-complete-modal"
    >
      <motion.div
        initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.3, ease: [0.2, 0.7, 0.2, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="lux-card w-full max-w-md p-6"
      >
        <div className="lux-eyebrow">Complete appointment</div>
        <h2 className="text-[16px] font-semibold tracking-tight text-nexa-ink mt-1">
          {appointment.customer_name}
        </h2>
        <p className="text-[10.5px] text-nexa-mute mt-0.5">
          {appointment.service} · ₹{appointment.amount || 0}
        </p>

        <div className="mt-5">
          <div className="text-[11px] font-medium text-nexa-ink mb-2.5">
            Was payment received?
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setChoice("yes")}
              data-testid="apt-complete-yes"
              className={`h-11 rounded-xl border text-[11px] font-medium transition-all ${
                choice === "yes"
                  ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                  : "border-nexa-line bg-white text-nexa-ink hover:border-nexa-ink/40"
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                Yes, received
              </span>
            </button>
            <button
              type="button"
              onClick={() => setChoice("no")}
              data-testid="apt-complete-no"
              className={`h-11 rounded-xl border text-[11px] font-medium transition-all ${
                choice === "no"
                  ? "border-amber-500 bg-amber-50 text-amber-700"
                  : "border-nexa-line bg-white text-nexa-ink hover:border-nexa-ink/40"
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" strokeWidth={1.8} />
                No, pending
              </span>
            </button>
          </div>
          {choice === "no" && (
            <p className="mt-2.5 text-[9.5px] text-nexa-mute">
              You can mark this paid later from the row menu.
            </p>
          )}
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="lux-btn-secondary">Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={choice === null || saving}
            data-testid="apt-complete-submit"
            className="lux-btn-accent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : "Confirm"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function CreateModal({ onClose, onCreated, defaultDate }) {
    const initial = useMemo(() => {
    const d = defaultDate
      ? new Date(`${defaultDate}T10:00:00`)
      : new Date(Date.now() + 60 * 60 * 1000);

    const pad = (n) => String(n).padStart(2, "0");

    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, [defaultDate]);

  const [slotsByStaff, setSlotsByStaff] = useState({});
  const staffManuallySelected = useRef(false);
  const [form, setForm] = useState({
    customer_name: "", phone: "",
    service: "", service_id: "",
    duration_min: 30,
    start_time: initial,
    staff_id: "", staff_name: "",
    amount: "", is_vip: false, notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [rec, setRec] = useState(null);       // { options, recommended_member_id, has_available }
  const [recLoading, setRecLoading] = useState(false);
  const [recError, setRecError] = useState(null);
  const [slots, setSlots] = useState([]);     // next-available slots when no staff for the picked time
  const [services, setServices] = useState(null); // active services catalog
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Load active services on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/services", { params: { active_only: true } });
        setServices(data.services || []);
      } catch { setServices([]); }
    })();
  }, []);

  const pickService = (svc) => {
    staffManuallySelected.current = false;

    setForm((f) => ({
      ...f,
      service_id: svc.service_id,
      service: svc.name,
      duration_min: svc.duration_min,
      amount: String(svc.amount ?? ""),
      staff_id: "",
      staff_name: "",
    }));
  };

  // Debounced fetch of staff recommendations whenever service+start+duration are all set.
  const canRecommend =
    form.service.trim() &&
    form.start_time &&
    Number(form.duration_min) > 0;

  useEffect(() => {
    if (!canRecommend) {
      setRec(null);
      setSlots([]);
      setSlotsByStaff({});
      return;
    }

    let cancelled = false;
    setRecLoading(true);
    setRecError(null);
    setSlotsByStaff({});

    const t = setTimeout(async () => {
      try {
        const iso = new Date(form.start_time).toISOString();

        const { data } = await api.get(
          "/appointments/staff-recommendations",
          {
            params: {
              service: form.service,
              service_id: form.service_id || undefined,
              start_time: iso,
              start_time_local: form.start_time,
              duration_min: Number(form.duration_min),
            },
          }
        );

        if (cancelled) return;

        setRec(data);

        const availableNow = data.options || [];
        const staffOptions = data.staff_options || availableNow;

        // Only auto-select while the user has not chosen a doctor manually.
        if (availableNow.length > 0) {
          if (!staffManuallySelected.current) {
            const top =
              availableNow.find(
                (o) => o.member_id === data.recommended_member_id
              ) || availableNow[0];

            setForm((f) => ({
              ...f,
              staff_id: top.member_id,
              staff_name: top.name,
            }));
          }
        } else if (
          !staffManuallySelected.current
        ) {
          setForm((f) => ({
            ...f,
            staff_id: "",
            staff_name: "",
          }));
        }

        // Fetch the next three slots separately for every eligible doctor.
        const anchorLocal = form.start_time.slice(0, 16);

        const slotEntries = await Promise.all(
          staffOptions
            .filter((staff) => staff.member_id)
            .map(async (staff) => {
              try {
                const { data: slotData } = await api.get(
                  "/appointments/next-available-slots",
                  {
                    params: {
                      service: form.service,
                      service_id: form.service_id || undefined,
                      after: iso,
                      after_local: form.start_time,
                      duration_min: Number(form.duration_min),
                      limit: 100,
                      staff_member_id: staff.member_id,
                    },
                  }
                );

                // For a doctor who is free now, don't duplicate the
                // currently selected time among their upcoming slots.
                
                
                const selectedDate = (form.start_time || "").slice(0, 10);
                const selectedAnchor = (form.start_time || "").slice(0, 16);

                const futureSlots = (slotData.slots || []).filter((slot) => {
                  const slotLocal = slot.start_time_local || "";

                  return (
                    slotLocal.slice(0, 10) === selectedDate &&
                    slotLocal.slice(0, 16) > selectedAnchor
                  );
                });

                return [staff.member_id, futureSlots];
              } catch {
                return [staff.member_id, []];
              }
            })
        );

        if (cancelled) return;

        setSlotsByStaff(Object.fromEntries(slotEntries));

        // Keep the existing general alternative-slot fallback when there
        // are no eligible staff options to display.
        if (staffOptions.length === 0) {
          try {
            const { data: slotData } = await api.get(
              "/appointments/next-available-slots",
              {
                params: {
                  service: form.service,
                  service_id: form.service_id || undefined,
                  after: iso,
                  after_local: form.start_time,
                  duration_min: Number(form.duration_min),
                  limit: 100,
                },
              }
            );

            if (!cancelled) setSlots(slotData.slots || []);
          } catch {
            if (!cancelled) setSlots([]);
          }
        } else {
          setSlots([]);
        }
      } catch (err) {
        if (!cancelled) {
          setRecError(
            formatApiError(err.response?.data?.detail) ||
              "Could not load staff"
          );
          setRec(null);
          setSlotsByStaff({});
        }
      } finally {
        if (!cancelled) setRecLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [
    form.service,
    form.service_id,
    form.start_time,
    form.duration_min,
  ]);

  const selectedStaffAvailable =
    !recLoading &&
    (rec?.options || []).some(
      (o) => o.member_id === form.staff_id
    );
    
    // All mandatory fields — used to enable/disable the submit button.
  const hasAllRequired = (
    form.customer_name.trim().length > 0 &&
    isSubmittablePhone(form.phone, { required: true }) &&
    form.service_id &&
    form.start_time &&
    Number(form.duration_min) > 0 &&
    form.staff_id &&
    selectedStaffAvailable &&
    Number(form.amount) > 0
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!hasAllRequired) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSaving(true);
    try {
      const iso = new Date(form.start_time).toISOString();
      // Final availability re-check (race-safe): another appointment may have
      // been created for this staff since the form was opened.
      const { data: check } = await api.get("/appointments/staff-recommendations", {
        params: {
          service: form.service,
          service_id: form.service_id || undefined,
          start_time: iso,
          start_time_local: form.start_time,
          duration_min: Number(form.duration_min),
        },
      });
      const stillAvailable = (check.options || []).some(
        (o) => o.member_id === form.staff_id,
      );
      if (!stillAvailable) {
        toast.error("Selected staff is no longer available for this time.", {
          description:
            "Choose an available slot for your selected doctor, or select another time.",
        });

        setRec(check);
        return;
      }

      const { data } = await api.post("/appointments", {
        ...form,
        start_time: iso,
        duration_min: Number(form.duration_min),
        amount: Number(form.amount),
        service_id: form.service_id || undefined,
        staff_member_id: form.staff_id || undefined,
        staff: form.staff_name,          // legacy string field for existing code paths
      });
      const tail = waTail(data?.notification, form.customer_name);
      toast.success("Appointment confirmed.", { description: tail || undefined });
      onCreated(); onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create");
    } finally { setSaving(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-nexa-accent/40 backdrop-blur-[2px] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.form
        initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
        onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="nexa-apt-create-modal lux-card w-full max-w-3xl p-6 max-h-[86vh] overflow-y-auto"
        data-testid="apt-create-modal"
      >
        <div className="lux-eyebrow">New</div>
        <h2 className="text-[17px] font-semibold tracking-tight text-nexa-ink mt-1 mb-5">Add an appointment</h2>
        <div className="space-y-4">
          <div>
            <label className="lux-label">Customer <span className="text-red-500">*</span></label>
            <input data-testid="apt-form-name" className="lux-input" value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} placeholder="Full name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="lux-label">Phone <span className="text-red-500">*</span></label>
              <PhoneInput value={form.phone} onChange={(v) => update("phone", v)} testId="apt-form-phone" required={true} />
            </div>
            <div>
              <label className="lux-label">Service <span className="text-red-500">*</span></label>
              <ServicePicker
                services={services}
                value={form.service_id}
                onPick={pickService}
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="lux-label">Starts at <span className="text-red-500">*</span></label>
              <CompactDateTimePicker
                value={form.start_time}
                onChange={(value) => update("start_time", value)}
              />
            </div>
            <div>
              <label className="lux-label">Duration (m) <span className="text-red-500">*</span></label>
              <input data-testid="apt-form-duration" type="number" min="5" className="lux-input" value={form.duration_min} onChange={(e) => update("duration_min", e.target.value)} />
            </div>
          </div>

          {/* Staff — smart recommendation dropdown */}
          <div>
            <label className="lux-label flex items-center gap-1.5">
              Staff <span className="text-red-500">*</span>
              {recLoading && <Loader2 className="w-3 h-3 animate-spin text-nexa-mute" strokeWidth={1.6} />}
            </label>
            <StaffPicker
              rec={rec}
              slots={slots}
              slotsByStaff={slotsByStaff}
              value={form.staff_id}
              onChange={(id, name) => {
                staffManuallySelected.current = true;

                setForm((f) => ({
                  ...f,
                  staff_id: id,
                  staff_name: name,
                }));
              }}
              onPickSlot={(slot) => {
                staffManuallySelected.current = true;

                setForm((f) => ({
                  ...f,
                  start_time: slot.start_time_local,
                  staff_id: slot.member_id || f.staff_id,
                  staff_name: slot.staff || f.staff_name,
                }));
              }}
              onPickStaffSlot={(staff, slot) => {
                staffManuallySelected.current = true;

                setForm((f) => ({
                  ...f,
                  staff_id: staff.member_id,
                  staff_name: staff.name,
                  start_time: slot.start_time_local,
                }));
              }}
              disabled={!canRecommend || recLoading}
              hint={!canRecommend
                ? "Fill service, date and duration to see staff."
                : null}
              error={recError}
              contextLocal={form.start_time}
              contextDuration={Number(form.duration_min)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="lux-label">Amount (₹) <span className="text-red-500">*</span></label>
              <input data-testid="apt-form-amount" type="number" min="1" className="lux-input" value={form.amount} onChange={(e) => update("amount", e.target.value)} placeholder="500" />
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-[13px] text-nexa-mute cursor-pointer h-9">
                <input type="checkbox" className="accent-nexa-accent" checked={form.is_vip} onChange={(e) => update("is_vip", e.target.checked)} /> VIP customer
              </label>
            </div>
          </div>
        </div>
        <div className="mt-6 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="lux-btn-secondary">Cancel</button>
          <button
            data-testid="apt-form-submit"
            type="submit"
            disabled={saving || !hasAllRequired}
            className="lux-btn-accent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : "Create appointment"}
          </button>
        </div>
      </motion.form>
    </motion.div>
  );
}

// ---- Staff picker ----
function formatSlotLabel(iso_local, contextLocal) {
  try {
    const dt = new Date(iso_local + (iso_local.length === 16 ? ":00" : ""));
    const today = contextLocal ? new Date(contextLocal.slice(0, 10) + "T00:00:00") : new Date();
    const same = dt.toDateString() === today.toDateString();
    const time = dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    if (same) return `Today ${time}`;
    return `${dt.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })} · ${time}`;
  } catch { return iso_local; }
}

function formatDoctorSlotLabel(slotLocal, selectedLocal) {
  try {
    const dt = new Date(
      slotLocal + (slotLocal.length === 16 ? ":00" : "")
    );

    const time = dt.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });

    if (slotLocal.slice(0, 10) === selectedLocal.slice(0, 10)) {
      return time;
    }

    const date = dt.toLocaleDateString(undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
    });

    return `${date} · ${time}`;
  } catch {
    return slotLocal;
  }
}

function formatSlotRange(startLocal, durationMin) {
  try {
    const s = new Date(startLocal + (startLocal.length === 16 ? ":00" : ""));
    const e = new Date(s.getTime() + durationMin * 60000);
    const day = s.toLocaleDateString(undefined, { day: "numeric", month: "short" });
    const t = (d) => d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    return `${day} · ${t(s)} – ${t(e)}`;
  } catch { return ""; }
}

function ServicePicker({ services, value, onPick }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const selected = (services || []).find((s) => s.service_id === value);

  if (services === null) {
    return <div className="lux-input flex items-center text-[11px] text-nexa-mute">Loading services…</div>;
  }
  if (services.length === 0) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-3.5 py-2.5 text-[10.5px] text-amber-800" data-testid="apt-form-service-empty">
        No active services. <a href="/business-settings/services" className="underline font-medium">Add one</a> to book appointments.
      </div>
    );
  }

  const filtered = services.filter((s) => !q.trim() || s.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="relative">
      <input
        type="text"
        value={open ? q : (selected?.name || "")}
        onFocus={() => { setOpen(true); setQ(""); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        placeholder="Search service…"
        className="lux-input"
        data-testid="apt-form-service"
      />
      {open && (
        <div
          className="nexa-service-menu"
          data-testid="apt-form-service-list"
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-[10.5px] text-nexa-mute">
              No matching service.
            </div>
          ) : (
            filtered.map((s) => (
              <button
                key={s.service_id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onPick(s);
                  setOpen(false);
                  setQ("");
                }}
                data-testid={`apt-form-service-opt-${s.service_id}`}
                className={`nexa-service-option ${
                  s.service_id === value ? "is-selected" : ""
                }`}
              >
                <span className="nexa-service-option-name">
                  {s.name}
                </span>

                <span className="nexa-service-option-meta">
                  {s.duration_min} min · ₹
                  {Number(s.amount || 0).toLocaleString("en-IN")}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/* Staff profile image or initials fallback */
function StaffAvatar({ name, image, size = 30 }) {
  const avatarInitials =
    (name || "•")
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase() || "•";

  const style = { width: size, height: size };

  if (image) {
    return (
      <img
        src={image}
        alt={name || "Staff"}
        className="nexa-staff-avatar"
        style={style}
        data-testid="apt-staff-avatar"
      />
    );
  }

  return (
    <div
      className="nexa-staff-avatar nexa-staff-avatar-fallback"
      style={style}
      data-testid="apt-staff-avatar-fallback"
    >
      {avatarInitials}
    </div>
  );
}

/* Custom staff dropdown with photos and recommendations */
function StaffDropdown({
  available,
  value,
  onChange,
  disabled,
  recommendedId,
  slotsByStaff = {},
  currentLocalTime,
  onPickStaffSlot,
})  {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selected =
    available.find((member) => member.member_id === value) ||
    available.find((member) => member.member_id === recommendedId) ||
    available[0];

  const labelOf = (level) =>
    level === "expert"
      ? "Expert"
      : level === "advanced"
        ? "Advanced"
        : "Standard";

  const starsFor = (rank) =>
    "★".repeat(Math.max(1, Math.min(3, rank || 1)));

  const dimStars = (rank) =>
    "★".repeat(Math.max(0, 3 - (rank || 1)));

  useEffect(() => {
    if (!open) return;

    const handleOutside = (event) => {
      if (!wrapRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <div className="nexa-staff-picker" ref={wrapRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="apt-form-staff"
        className={`nexa-staff-trigger ${open ? "is-open" : ""}`}
      >
        {selected ? (
          <span className="nexa-staff-selected">
            <StaffAvatar
              name={selected.name}
              image={selected.profile_image}
              size={34}
            />

            <span className="nexa-staff-selected-copy">
              <span className="nexa-staff-name">
                {selected.name}
              </span>

              <span className="nexa-staff-expertise">
                <span className="nexa-staff-stars">
                  {starsFor(selected.expertise_rank)}
                  <span className="nexa-staff-dim-stars">
                    {dimStars(selected.expertise_rank)}
                  </span>
                </span>

                {labelOf(selected.expertise_level)}

                {selected.member_id === recommendedId && (
                  <span className="nexa-staff-recommended">
                    Recommended
                  </span>
                )}
              </span>
            </span>
          </span>
        ) : (
          <span>Select staff</span>
        )}

        <svg
          viewBox="0 0 12 12"
          width="14"
          height="14"
          className={`nexa-staff-chevron ${open ? "is-open" : ""}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          aria-hidden="true"
        >
          <path
            d="M3 4.5 6 7.5 9 4.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Available staff"
          className="nexa-staff-menu"
          data-testid="apt-form-staff-menu"
        >
          {available.map((o) => {
            const isSel = o.member_id === value;
            const isRec = o.member_id === recommendedId;
            const staffSlots = slotsByStaff[o.member_id] || [];

            const statusLabel = o.available
              ? "Available"
              : o.reason === "Already booked at this time"
                ? "Busy at selected time"
                : o.reason?.startsWith("Not working")
                  ? "Not working that day"
                  : o.reason?.startsWith("Working hours")
                    ? "Outside working hours"
                    : "Unavailable at selected time";

            return (
              <div
                key={o.member_id}
                className={`px-2.5 py-2 border-b border-nexa-line/50 last:border-0 ${
                  isSel ? "bg-nexa-canvas/50" : ""
                }`}
              >
                <button
                  type="button"
                  role="option"
                  aria-selected={isSel}
                  disabled={disabled}
                  onClick={() => {
                    onChange(o.member_id, o.name || "");
                    setOpen(false);
                  }}
                  data-testid={`apt-form-staff-opt-${o.member_id}`}
                  className="w-full flex items-center gap-2 text-left rounded-lg"
                >
                  <StaffAvatar
                    name={o.name}
                    image={o.profile_image}
                    size={28}
                  />

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[11.5px] font-medium text-nexa-ink">
                        {o.name}
                      </span>

                      {isRec && (
                        <span className="text-[8.5px] font-semibold text-emerald-700">
                          Recommended
                        </span>
                      )}
                    </div>

                    <div className="text-[9.5px] text-nexa-mute mt-0.5">
                      <span className="text-[#C49A36]">
                        {"★".repeat(Math.max(1, Math.min(3, o.expertise_rank || 1)))}
                      </span>
                      {" · "}
                      {o.expertise_level === "expert"
                        ? "Expert"
                        : o.expertise_level === "advanced"
                          ? "Advanced"
                          : "Standard"}
                      {o.role ? ` · ${o.role}` : ""}
                    </div>
                  </div>

                  <span
                    title={o.reason || statusLabel}
                    className={`shrink-0 rounded-full px-2 py-1 text-[9px] font-medium ${
                      o.available
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-amber-50 text-amber-800"
                    }`}
                  >
                    {statusLabel}
                  </span>

                  {isSel && (
                    <CheckCircle2
                      size={14}
                      className="text-emerald-700 shrink-0"
                    />
                  )}
                </button>

                <div className="ml-10 mt-2 flex flex-wrap items-center gap-1.5">
                  {o.available && currentLocalTime && (
                    <span className="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[9.5px] font-medium text-emerald-800">
                      Selected ·{" "}
                      {formatDoctorSlotLabel(
                        currentLocalTime,
                        currentLocalTime
                      )}
                    </span>
                  )}

                  {staffSlots.map((slot) => (
                    <button
                      key={slot.start_time}
                      type="button"
                      onClick={() => {
                        onPickStaffSlot?.(o, slot);
                        setOpen(false);
                      }}
                      className="rounded-md border border-[#E4D29A] bg-[#FFF8E7] px-2 py-1 text-[9.5px] font-medium text-[#72551A] hover:bg-[#FFF0C7] transition-colors"
                    >
                      {formatDoctorSlotLabel(
                        slot.start_time_local,
                        currentLocalTime
                      )}
                    </button>
                  ))}

                  {!o.available && staffSlots.length === 0 && (
                    <span className="text-[9px] text-nexa-mute">
                      No later slots available on this date.
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StaffPicker({
  rec,
  slots,
  slotsByStaff,
  value,
  onChange,
  onPickSlot,
  onPickStaffSlot,
  disabled,
  hint,
  error,
  contextLocal,
  contextDuration,
}) {   
  if (hint) {
    return (
      <div className="lux-input flex items-center text-[11px] text-nexa-mute" style={{ opacity: 0.7 }} data-testid="apt-form-staff-hint">
        {hint}
      </div>
    );
  }
  if (error) {
    return (
      <div className="lux-input flex items-center text-[11px] text-red-500" data-testid="apt-form-staff-error">
        {error}
      </div>
    );
  }
  if (!rec) {
    return (
      <div className="lux-input flex items-center text-[11px] text-nexa-mute" data-testid="apt-form-staff-loading">
        Checking availability…
      </div>
    );
  }

  const availableNow = rec.options || [];
  const staffOptions = rec.staff_options || availableNow;

  // Case 2 — nobody available.
  if (staffOptions.length === 0) {
    return (
      <div
        className="rounded-xl border border-amber-200 bg-amber-50/70 px-3.5 py-3"
        data-testid="apt-form-no-staff"
      >
        <div className="flex items-start gap-2">
          <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" aria-hidden />
          <div className="flex-1">
            <div className="text-[11.5px] font-medium text-amber-900">No staff available</div>
            <div className="mt-0.5 text-[10.5px] text-amber-700/90">
              No team member is available for{" "}
              <span className="font-medium">{formatSlotRange(contextLocal, contextDuration)}</span>.
            </div>
          </div>
        </div>

        {slots && slots.length > 0 && (
          <div className="mt-3 pt-3 border-t border-amber-200/60">
            <div className="text-[9.5px] tracking-[0.14em] uppercase font-semibold text-amber-800/80">
              Suggested available slots
            </div>
            <div className="mt-2 space-y-1" data-testid="apt-form-slot-list">
              {slots.map((s) => (
                <button
                  key={s.start_time}
                  type="button"
                  onClick={() => onPickSlot(s)}
                  data-testid={`apt-form-slot-${s.start_time}`}
                  className="w-full flex items-center justify-between gap-2 text-left rounded-lg px-3 py-2 bg-white/70 border border-amber-200/70 hover:bg-white hover:border-amber-300 transition-colors"
                >
                  <span className="text-[11px] text-nexa-ink">{formatSlotLabel(s.start_time_local, contextLocal)}</span>
                  <span className="text-[10px] text-nexa-mute">{s.staff}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Case 1 — one or more available.
  const recommended =
    availableNow.find(
      (o) => o.member_id === rec.recommended_member_id
    ) ||
    availableNow[0] ||
    staffOptions[0];
  const labelOf = (lvl) => lvl === "expert" ? "Expert" : lvl === "advanced" ? "Advanced" : "Standard";
  const starsFor = (rank) => "★".repeat(Math.max(1, Math.min(3, rank || 1)));
  const dimStars = (rank) => "★".repeat(Math.max(0, 3 - (rank || 1)));
  const cur =
    staffOptions.find((o) => o.member_id === value) ||
    recommended;
  return (
    <div className="space-y-2" data-testid="apt-form-staff-list">
      <StaffDropdown
        available={staffOptions}
        value={value}
        onChange={onChange}
        disabled={disabled}
        recommendedId={rec.recommended_member_id}
        slotsByStaff={slotsByStaff}
        currentLocalTime={contextLocal}
        onPickStaffSlot={onPickStaffSlot}
      />

      {/* Expertise + reason detail for the current selection */}
      {cur && (
        <div className="flex items-center gap-2 pt-0.5 text-[10px] text-nexa-mute" data-testid="apt-form-staff-detail">
          <span className="tracking-[0.05em]">
            <span className="text-[#F3C55A]">{starsFor(cur.expertise_rank)}</span>
            <span className="text-nexa-line">{dimStars(cur.expertise_rank)}</span>
          </span>
          <span className="text-nexa-ink font-medium">{labelOf(cur.expertise_level)}</span>
          {cur.member_id === rec.recommended_member_id && (
            <span className="text-nexa-mute">· Recommended</span>
          )}
        </div>
      )}

      {/* Nexa Recommendation helper */}
      
      {/* Doctor availability helper */}
      <div
        className="flex items-start gap-2 rounded-lg bg-nexa-canvas/70 border border-nexa-line px-3 py-2"
        data-testid="apt-form-nexa-tip"
      >
        <Sparkle
          className="w-3 h-3 mt-[3px] text-nexa-accent shrink-0"
          strokeWidth={1.8}
          aria-hidden
        />

        <div className="text-[10.5px] leading-4 text-nexa-mute">
          <div className="font-semibold text-nexa-ink">
            {cur.available && cur.member_id === rec.recommended_member_id
              ? "AI Recommended"
              : "Doctor availability"}
          </div>

          <div className="mt-1 font-medium text-nexa-ink">
            {cur.name}
          </div>

          <div className="mt-1">
            {cur.available
              ? "✓ Available at the selected time. Future slots are listed above."
              : `${cur.reason || "Not available at the selected time"}. Choose one of the suggested slots above.`}
          </div>
        </div>
      </div>
    </div>
  );
}
